import { Form, FormikProvider, useFormik } from 'formik';
import { UploadFileFormValidValues, UploadFileFormValues, UploadFormDialogProps } from './dto';
import * as yup from 'yup';
import FileUpload from 'components/file-upload/FileUpload';
import { ACCEPTED_DATA_FILE_TYPES } from '../../consts';
import {
    Alert,
    Button,
    CardContent,
    DialogTitle,
    Divider,
    FormHelperText,
    Grid,
    InputLabel,
    Stack,
    TextField,
} from '@mui/material';
import { useCallback, useLayoutEffect, useRef } from 'react';
import { LoadingButton } from '@mui/lab';
import { FormattedMessage } from 'react-intl';

const UploadFileDialogSchema = yup.object({
    file: yup
        .mixed<File>()
        .nullable()
        .required('Spreadsheet file is required')
        .test('is-valid-file', 'Invalid file', (value) => value instanceof File),
    fileName: yup.string().required('File name is required'),
});

export const UploadFileDialog = ({ initialValues = {}, errorMessage, onSubmit, onCancel }: UploadFormDialogProps) => {
    const formik = useFormik<UploadFileFormValues>({
        initialValues: {
            file: null,
            fileName: initialValues?.fileName || '',
        },
        onSubmit: async (values) => {
            await onSubmit?.(values as UploadFileFormValidValues);
        },
        validationSchema: UploadFileDialogSchema,
    });
    const {
        handleSubmit,
        touched,
        errors,
        setFieldTouched,
        setValues,
        values,
        getFieldProps,
        isSubmitting,
        isValid,
        dirty,
    } = formik;
    const onFileSet = useCallback(
        (field: string, value: File) => {
            setValues({
                file: value,
                fileName: value?.name ?? '',
            });
        },
        [setValues],
    );

    const fileNameInputRef = useRef<HTMLInputElement>(null);
    useLayoutEffect(() => {
        const fileName = values.file?.name;
        const fileNameInputElement = fileNameInputRef.current?.querySelector?.('input');
        if (fileNameInputElement && fileName) {
            fileNameInputElement.focus();
            fileNameInputElement.setSelectionRange(
                0,
                fileName.includes('.') ? fileName.lastIndexOf('.') : fileName.length,
            );
        }
    }, [values.file]);

    return (
        <FormikProvider value={formik}>
            <Form onSubmit={handleSubmit}>
                <DialogTitle>
                    <FormattedMessage id="inside-fifa.generic-tables.upload-file" />
                </DialogTitle>
                <CardContent>
                    <Grid container spacing={3}>
                        {errorMessage && (
                            <Grid item xs={12} rowGap={3}>
                                <Alert severity="error">{errorMessage}</Alert>
                            </Grid>
                        )}
                        <Grid item xs={12} rowGap={3}>
                            <Stack spacing={1} component="div">
                                <FileUpload
                                    name="file"
                                    accept={ACCEPTED_DATA_FILE_TYPES}
                                    setFieldValue={onFileSet}
                                    setFieldTouched={setFieldTouched}
                                    file={values.file}
                                    error={touched.file && !!errors.file}
                                    disabled={isSubmitting}
                                />
                                {errors.file}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} rowGap={3}>
                            <Stack spacing={1}>
                                <InputLabel>
                                    <FormattedMessage id="inside-fifa.generic-tables.file-name" />
                                </InputLabel>
                                <TextField
                                    ref={fileNameInputRef}
                                    fullWidth
                                    id="fileName"
                                    error={touched.fileName && Boolean(errors.fileName)}
                                    helperText={touched.fileName && errors.fileName}
                                    disabled={isSubmitting}
                                    {...getFieldProps('fileName')}
                                />
                                <FormHelperText>
                                    <FormattedMessage id="inside-fifa.generic-tables.file-name-override-field-helper-text" />
                                </FormHelperText>
                            </Stack>
                        </Grid>
                    </Grid>
                </CardContent>
                <Divider />
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ px: 2.5, py: 2 }}>
                    <Button color="error" size="small" disabled={isSubmitting} onClick={onCancel}>
                        <FormattedMessage id="inside-fifa.cancel" />
                    </Button>
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        size="small"
                        loading={isSubmitting}
                        disabled={!isValid || !dirty}
                    >
                        <FormattedMessage id="inside-fifa.generic-tables.upload-action" />
                    </LoadingButton>
                </Stack>
            </Form>
        </FormikProvider>
    );
};
